import { Checkbox, InputNumber, DatePicker, Row, Col, Form } from 'antd';
import React, { FC, useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeviceType,
  LicenseCreationAttributes,
  LicenseManagerAssignmentAttributes,
  deviceTypeLabels,
  deviceTypeSorter,
  getUnlimitedLicenseAttributes,
} from '../../../stores/licensing-store';
import moment from 'moment';
import { isArray } from 'util';
import { getDateFormat } from 'src/common/utils';
import { FormInstance } from 'antd/lib';

const LicenseAttributesComponent: FC<{
  onChange: (item: LicenseManagerAssignmentAttributes) => void;
  onFormValidation?: (errors: boolean) => void;
  initialAttr?: LicenseCreationAttributes;
  roleExpirationDate?: Date;
  form?: FormInstance;
}> = ({ onChange, initialAttr, roleExpirationDate, onFormValidation = undefined, form }) => {
  const { t } = useTranslation();
  const [licenseAttributesForm] = Form.useForm();
  //const [customError, setCustomError] = useState('');
  const initialMaximumLicenseDueDate = (initialAttr && moment(initialAttr.maximumLicenseDueDate)) || moment();
  //const initialExpirationDate = (roleExpirationDate && moment(roleExpirationDate)) || moment();
  const options = Object.values(DeviceType)
    .toSorted(deviceTypeSorter)
    .map((y) => {
      return {
        label: deviceTypeLabels[y],
        value: y,
      };
    });
  const [checkedDevices, setCheckedDevices] = useState<DeviceType[]>((initialAttr && initialAttr.allowedDevices) || []);
  const [canCreateUnlimitedLicense, setCanCreateUnlimitedLicense] = useState<boolean>(initialAttr?.canCreateUnlimitedLicense || false);
  const canCreateUnlimitedLicenseJustChanged = useRef(false);
  const unlimitedLicenseDefaults = getUnlimitedLicenseAttributes();

  useEffect(() => {
    if (canCreateUnlimitedLicenseJustChanged.current) {
      (form ?? licenseAttributesForm).setFields([
        { name: 'maxLicensesPeriodInDays', value: '' },
        { name: 'maximumLicenseDueDate', value: '' },
      ]);
      (form ?? licenseAttributesForm).validateFields();
    }
  }, [canCreateUnlimitedLicense]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line arrow-body-style
  const disabledDate = (current: any) => {
    // Can not select days before today and today
    return current && current < moment.now();
  };
  const ChangeValue = async (checkedValues: any) => {
    let values = checkedDevices;

    if (isArray(checkedValues)) {
      values = checkedValues.map((z) => z as DeviceType);
      setCheckedDevices(values);
    }
    try {
      await (form ?? licenseAttributesForm).validateFields();
      let canCreateUnlimitedLicense = (form ?? licenseAttributesForm).getFieldValue('canCreateUnlimitedLicense');
      let item: any = {
        allowedDevices: values,
        maxNumberOfLicensesPerMonth: (form ?? licenseAttributesForm).getFieldValue('maxNumberOfLicensesPerMonth'),
        maxLicensesPeriodInDays: canCreateUnlimitedLicense
          ? unlimitedLicenseDefaults.maxPeriodInDays
          : (form ?? licenseAttributesForm).getFieldValue('maxLicensesPeriodInDays'),
        maximumLicenseDueDate: canCreateUnlimitedLicense
          ? unlimitedLicenseDefaults.untilDate
          : (form ?? licenseAttributesForm).getFieldValue('maximumLicenseDueDate')?.toDate(),
        // roleExpirationDate: form.getFieldValue('roleExpirationDate').toDate(),
        needsSignOff: (form ?? licenseAttributesForm).getFieldValue('needsSignOff'),
        canCreateRecurrentLicenses: (form ?? licenseAttributesForm).getFieldValue('canCreateRecurrentLicenses'),
        canCreateUnlimitedLicense: (form ?? licenseAttributesForm).getFieldValue('canCreateUnlimitedLicense'),
      };
      onChange(item);
      onFormValidation && onFormValidation(false);
    } catch {
      onFormValidation && onFormValidation(true);
      return;
    }
  };
  const onCanCreateUnlimitedLicenseChange = async (e: any) => {
    setCanCreateUnlimitedLicense(e.target.checked);

    canCreateUnlimitedLicenseJustChanged.current = !e.target.checked;
    await ChangeValue(undefined);
    if (e.target.checked) onFormValidation && onFormValidation(false);
  };
  const getCheckBoxesDefaultValue = () => {
    return options.map((o) => (initialAttr ? initialAttr.allowedDevices.any((x) => x === o.value) && o.value : false));
  };
  return (
    <Form form={form ?? licenseAttributesForm} layout="inline" autoComplete="off">
      <Form.Item
        name={'maxNumberOfLicensesPerMonth'}
        rules={[{ required: true, message: t('PleaseEnterMaxNumberOfLicensesPerMonth') }]}
        initialValue={initialAttr?.maxNumberOfLicensesPerMonth}
        style={{ width: 'calc(47%)' }}
        label={t('MaxNumberOfLicensesPerMonth')}
      >
        <InputNumber autoComplete="off" precision={0} placeholder={t('MaxNumberOfLicensesPerMonth')} onChange={ChangeValue} min={0} />
      </Form.Item>
      {!canCreateUnlimitedLicense && (
        <>
          <Form.Item
            name={'maxLicensesPeriodInDays'}
            style={{ width: 'calc(45%)' }}
            initialValue={initialAttr?.maxLicensesPeriodInDays}
            rules={[{ required: !canCreateUnlimitedLicense, message: t('PleaseEnterMaxLicensesPeriodInDays') }]}
            label={t('MaxLicensesPeriodInDays')}
          >
            <InputNumber autoComplete="off" precision={0} disabled={canCreateUnlimitedLicense} onChange={ChangeValue} min={0} />
          </Form.Item>

          <Form.Item
            name={'maximumLicenseDueDate'}
            rules={[{ required: !canCreateUnlimitedLicense, message: t('PleaseEnterMaxDueDate') }]}
            style={{ marginTop: 20, width: 'calc(47%)' }}
            label={t('MaximumLicenseDueDate')}
            initialValue={initialMaximumLicenseDueDate}
          >
            <DatePicker format={getDateFormat()} disabledDate={disabledDate} disabled={canCreateUnlimitedLicense} onChange={ChangeValue} />
          </Form.Item>
        </>
      )}
      {/*       <Form.Item
        name={'roleExpirationDate'}
        rules={[{ required: true, message: t('PleaseEnterExpirationDate') }]}
        style={{ marginTop: canCreateUnlimitedLicense ? 0 : 20, width: 'calc(45%)' }}
        initialValue={initialExpirationDate}
        label={t('RoleExpirationDate')}
      >
        <DatePicker format={getDateFormat()} disabledDate={disabledDate} onChange={ChangeValue} />
      </Form.Item> */}
      <Form.Item
        style={{ marginTop: 20, width: 'calc(100%)' }}
        name={'canCreateUnlimitedLicense'}
        initialValue={initialAttr?.canCreateUnlimitedLicense}
        valuePropName="checked"
      >
        <Checkbox onChange={onCanCreateUnlimitedLicenseChange}>{t('CanCreateUnlimitedLicense')}</Checkbox>
      </Form.Item>
      <Form.Item
        style={{ width: 'calc(100%)' }}
        name={'canCreateRecurrentLicenses'}
        initialValue={initialAttr?.canCreateRecurrentLicenses}
        valuePropName="checked"
      >
        <Checkbox onChange={ChangeValue}>{t('CanCreateRecurrentLicenses')}</Checkbox>
      </Form.Item>
      <Form.Item style={{ width: 'calc(45%)' }} name={'needsSignOff'} initialValue={initialAttr?.needsSignOff} valuePropName="checked">
        <Checkbox onChange={ChangeValue}>{t('NeedsSignOff')}</Checkbox>
      </Form.Item>
      <Form.Item
        initialValue={getCheckBoxesDefaultValue()}
        style={{ marginTop: 20, marginBottom: 20, width: '100%' }}
        label={t('AllowedDevices')}
        name={'allowedDevices'}
        rules={[
          () => ({
            validator(_, value: boolean[]) {
              if (value && value.every((v) => !v)) return Promise.reject(t('PleaseSelectAtLeastOneDeviceType'));
              return Promise.resolve();
            },
          }),
        ]}
      >
        <Checkbox.Group onChange={ChangeValue}>
          <Row>
            {options.map((option, i) => (
              <Col key={option.value} sm={i % 2 === 0 ? 13 : 11} xs={24}>
                <Checkbox value={option.value}>{t(option.label)}</Checkbox>
              </Col>
            ))}
          </Row>
        </Checkbox.Group>
      </Form.Item>
    </Form>
  );
};

export default LicenseAttributesComponent;
